import { activeCouncils } from '../config/registry';

const C = {
  bg: '#0a0f1a',
  bg2: '#0b1d34',
  panel: '#0f2139',
  panel2: '#102947',
  border: '#263b5d',
  amber: '#e8a830',
  amber2: '#f0c564',
  text: '#e8e0d8',
  muted: '#aeb8c7',
  dim: '#7f8da3',
  green: '#4ec06d',
  blue: '#7eb3f4',
  orange: '#d47850',
};

function LogoMark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 38,
        height: 38,
        borderRadius: 7,
        background: C.amber,
        color: '#071423',
        display: 'grid',
        placeItems: 'center',
        fontFamily: 'Georgia, serif',
        fontSize: 21,
        fontWeight: 800,
        border: '1px solid rgba(255,255,255,0.18)',
      }}>S</div>
      <div>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: 19, fontWeight: 800, color: C.text, lineHeight: 1 }}>SiteScope</div>
        <div style={{ color: C.dim, fontSize: 9, letterSpacing: 1.4, textTransform: 'uppercase', marginTop: 3 }}>DA Tracking & Intelligence</div>
      </div>
    </div>
  );
}

function MiniBadge({ children, tone = 'blue' }) {
  const tones = {
    blue: { color: C.blue, border: 'rgba(126,179,244,0.42)', bg: 'rgba(126,179,244,0.12)' },
    amber: { color: C.amber2, border: 'rgba(212,168,74,0.42)', bg: 'rgba(212,168,74,0.12)' },
    green: { color: C.green, border: 'rgba(78,192,109,0.38)', bg: 'rgba(78,192,109,0.12)' },
    orange: { color: C.orange, border: 'rgba(212,120,80,0.38)', bg: 'rgba(212,120,80,0.12)' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: 22,
      padding: '2px 8px',
      borderRadius: 5,
      border: `1px solid ${t.border}`,
      background: t.bg,
      color: t.color,
      fontSize: 10,
      fontWeight: 850,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function DashboardSnapshot() {
  const rows = [
    ['2026/5089.1', '56 Habitat Wy, Lennox Head NSW 2478', 'Ballina', 'New Dwelling', 'Lodged', '$1.1M'],
    ['DA26/0470', '4 Culgoora Street Kings Forest NSW 2487', 'Tweed', 'New Dwelling', 'Under Assessment', '$561K'],
    ['5.2026.176.1', '5A Spring Avenue Goonellabah NSW 2480', 'Lismore', 'New Dwelling', 'Submitted', '$140K'],
  ];
  return (
    <div className="ss-shot ss-shot-dashboard" aria-label="SiteScope desktop dashboard preview">
      <div className="ss-shot-top">
        <LogoMark />
        <div className="ss-shot-nav"><span>Dashboard</span><span>Map</span><span>Tracked</span></div>
      </div>
      <div className="ss-shot-stats">
        <div><span>Total</span><strong>4148</strong></div>
        <div><span>Assessing</span><strong>695</strong></div>
        <div><span>Exhibition</span><strong>45</strong></div>
        <div><span>Approved</span><strong>3029</strong></div>
      </div>
      <div className="ss-shot-search">Search DAs, suburbs, owners and builders</div>
      <div className="ss-shot-table">
        <div className="ss-shot-row ss-shot-head"><span>DA number</span><span>Address</span><span>Council</span><span>Category</span><span>Status</span><span>Est. cost</span></div>
        {rows.map(row => (
          <div className="ss-shot-row" key={row[0]}>
            <span>{row[0]}</span>
            <strong>{row[1]}</strong>
            <span>{row[2]}</span>
            <span>{row[3]}</span>
            <span>{row[4]}</span>
            <strong>{row[5]}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackedSnapshot() {
  return (
    <div className="ss-shot ss-shot-compact" aria-label="SiteScope tracked DA preview">
      <div className="ss-shot-label">Tracked DAs</div>
      <h3>Know what changed before the site meeting</h3>
      <div className="ss-tracked-item">
        <div><strong>112 Shirley Lane, Byron Bay</strong><span>Submission closes 2 May</span></div>
        <MiniBadge tone="orange">On Exhibition</MiniBadge>
      </div>
      <div className="ss-tracked-item">
        <div><strong>22 Kingsley St, Byron Bay</strong><span>Changed 5 days ago</span></div>
        <MiniBadge tone="green">Approved</MiniBadge>
      </div>
      <div className="ss-tracked-note">Private pipeline notes, status alerts and source-backed updates in one place.</div>
    </div>
  );
}

function ProSnapshot() {
  return (
    <div className="ss-shot ss-shot-compact" aria-label="SiteScope Pro Intelligence preview">
      <div className="ss-shot-label">Pro Intelligence</div>
      <h3>From public DA documents to project signals</h3>
      <div className="ss-pro-grid">
        <div><span>Project</span><strong>Dwelling + studio</strong></div>
        <div><span>Status</span><strong>On exhibition</strong></div>
        <div><span>Dwellings</span><strong>1</strong></div>
        <div><span>Evidence</span><strong>Source linked</strong></div>
      </div>
      <div className="ss-tracked-note">Public-safe document summaries with paid contact and supply-chain fields kept behind login.</div>
    </div>
  );
}

function MapSnapshot() {
  return (
    <div className="ss-shot ss-shot-compact ss-map-shot" aria-label="SiteScope map preview">
      <div className="ss-shot-label">Map</div>
      <h3>See clusters by place, not just by list</h3>
      <div className="ss-map-grid" aria-hidden="true">
        <span style={{ left: '28%', top: '35%' }} />
        <span style={{ left: '54%', top: '46%' }} />
        <span style={{ left: '72%', top: '28%' }} />
        <span style={{ left: '42%', top: '68%' }} />
      </div>
      <div className="ss-tracked-note">Pins and watch zones help teams understand where new applications are landing across the region.</div>
    </div>
  );
}

function Feature({ title, body }) {
  return (
    <article className="ss-feature">
      <h3>{title}</h3>
      <p>{body}</p>
    </article>
  );
}

function Stat({ value, label }) {
  return (
    <div className="ss-stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function Audience({ title, body }) {
  return (
    <article className="ss-audience">
      <strong>{title}</strong>
      <span>{body}</span>
    </article>
  );
}

function Price({ title, price, body }) {
  return (
    <article className="ss-price">
      <span>{title}</span>
      <strong>{price}</strong>
      <p>{body}</p>
    </article>
  );
}

function CouncilLink({ href, name, detail }) {
  return (
    <a className="ss-council-link" href={href}>
      <strong>{name}</strong>
      <span>{detail}</span>
    </a>
  );
}

const COUNCIL_PAGE_DETAILS = {
  Byron: 'Byron Bay, Bangalow, Mullumbimby and nearby localities.',
  Ballina: 'Ballina, Lennox Head, Wollongbar and coastal growth areas.',
  Lismore: 'Lismore, Goonellabah, Clunes and surrounding villages.',
  Tweed: 'Kingscliff, Tweed Heads, Pottsville and the Tweed coast.',
};

function councilPageSlug(council) {
  return String(council.name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function PublicLandingPage() {
  const councils = activeCouncils('northern-rivers');

  return (
    <main className="ss-landing">
      <style>{`
        .ss-landing { min-height: 100vh; background: ${C.bg}; color: ${C.text}; font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; overflow-x: hidden; }
        .ss-landing * { box-sizing: border-box; }
        .ss-header { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 14px clamp(18px, 4vw, 56px); background: rgba(7,20,35,0.88); border-bottom: 1px solid rgba(126,179,244,0.16); backdrop-filter: blur(12px); }
        .ss-header nav { display: flex; align-items: center; gap: 16px; color: ${C.muted}; font-size: 13px; font-weight: 750; }
        .ss-header a { color: inherit; text-decoration: none; }
        .ss-header .ss-login-link { color: ${C.amber2}; }
        .ss-hero { position: relative; min-height: min(760px, calc(100vh - 40px)); display: grid; align-items: end; padding: clamp(64px, 9vw, 120px) clamp(18px, 5vw, 72px) clamp(58px, 8vw, 96px); isolation: isolate; border-bottom: 1px solid rgba(126,179,244,0.14); }
        .ss-hero::before { content: ""; position: absolute; inset: 0; z-index: -3; background: linear-gradient(180deg, rgba(7,20,35,0.08), rgba(7,20,35,0.72) 54%, ${C.bg} 100%); }
        .ss-hero-scene { position: absolute; inset: 76px clamp(16px, 5vw, 72px) 34px; z-index: -4; opacity: 0.86; display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.62fr); gap: 20px; align-items: center; transform: perspective(1400px) rotateX(3deg); }
        .ss-hero-copy { max-width: 860px; }
        .ss-eyebrow { margin: 0 0 12px; color: ${C.amber2}; font-size: 12px; font-weight: 900; letter-spacing: 1.5px; text-transform: uppercase; }
        .ss-hero h1 { margin: 0; max-width: 760px; font-size: clamp(42px, 6.2vw, 82px); line-height: 0.98; letter-spacing: 0; font-weight: 850; }
        .ss-lead { max-width: 720px; margin: 20px 0 0; color: ${C.muted}; font-size: clamp(18px, 2.1vw, 23px); line-height: 1.45; }
        .ss-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 28px; }
        .ss-button { min-height: 46px; display: inline-flex; align-items: center; justify-content: center; padding: 0 18px; border-radius: 6px; border: 1px solid rgba(212,168,74,0.64); background: ${C.amber}; color: #071423; text-decoration: none; font-weight: 900; }
        .ss-button.secondary { background: rgba(7,20,35,0.58); color: ${C.text}; border-color: rgba(174,184,199,0.34); }
        .ss-button.text { background: transparent; color: ${C.amber2}; border-color: transparent; padding-left: 0; }
        .ss-section { padding: clamp(48px, 7vw, 88px) clamp(18px, 5vw, 72px); }
        .ss-section > h2 { margin: 0 0 14px; font-size: clamp(28px, 4vw, 48px); line-height: 1.05; letter-spacing: 0; }
        .ss-section > p { max-width: 760px; margin: 0 0 26px; color: ${C.muted}; font-size: 17px; line-height: 1.6; }
        .ss-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 34px; max-width: 980px; }
        .ss-stat { border: 1px solid ${C.border}; border-radius: 8px; background: ${C.bg2}; padding: 18px; }
        .ss-stat strong { display: block; color: ${C.amber2}; font-size: clamp(28px, 4vw, 42px); line-height: 1; }
        .ss-stat span { display: block; color: ${C.muted}; margin-top: 8px; font-size: 13px; line-height: 1.35; }
        .ss-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
        .ss-feature { border: 1px solid ${C.border}; border-radius: 8px; background: ${C.bg2}; padding: 20px; }
        .ss-feature h3 { margin: 0 0 10px; font-size: 18px; letter-spacing: 0; }
        .ss-feature p { margin: 0; color: ${C.muted}; line-height: 1.55; font-size: 14px; }
        .ss-shots { display: grid; grid-template-columns: 1.16fr 0.84fr; gap: 18px; align-items: stretch; }
        .ss-shot-stack { display: grid; gap: 18px; }
        .ss-shot { border: 1px solid ${C.border}; border-radius: 8px; background: #08182b; box-shadow: 0 24px 70px rgba(0,0,0,0.34); overflow: hidden; }
        .ss-shot-dashboard { min-height: 420px; padding: 18px; }
        .ss-shot-top { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding-bottom: 16px; border-bottom: 1px solid rgba(126,179,244,0.16); }
        .ss-shot-nav { display: flex; gap: 8px; color: ${C.muted}; font-size: 12px; font-weight: 800; }
        .ss-shot-nav span:first-child { color: ${C.amber2}; }
        .ss-shot-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-top: 18px; }
        .ss-shot-stats div { border: 1px solid rgba(126,179,244,0.22); border-radius: 7px; padding: 12px; background: #0d233d; }
        .ss-shot-stats span { display: block; color: ${C.dim}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.8px; }
        .ss-shot-stats strong { display: block; color: ${C.amber2}; font-size: 24px; margin-top: 4px; }
        .ss-shot-search { margin: 18px 0 10px; border: 1px solid rgba(126,179,244,0.24); border-radius: 7px; color: ${C.dim}; padding: 12px; font-size: 13px; }
        .ss-shot-row { display: grid; grid-template-columns: 0.9fr 2.4fr 0.8fr 1fr 1fr 0.8fr; gap: 10px; align-items: center; padding: 13px 12px; border-bottom: 1px solid rgba(126,179,244,0.14); color: ${C.muted}; font-size: 12px; }
        .ss-shot-row strong { color: ${C.text}; font-weight: 800; }
        .ss-shot-head { color: ${C.dim}; text-transform: uppercase; letter-spacing: 0.7px; font-size: 10px; }
        .ss-shot-compact { padding: 20px; min-height: 200px; }
        .ss-shot-label { color: ${C.amber2}; text-transform: uppercase; letter-spacing: 1.3px; font-size: 11px; font-weight: 900; margin-bottom: 10px; }
        .ss-shot h3 { margin: 0 0 16px; font-size: 22px; line-height: 1.15; letter-spacing: 0; }
        .ss-tracked-item { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; border-top: 1px solid rgba(126,179,244,0.14); }
        .ss-tracked-item strong, .ss-pro-grid strong { display: block; color: ${C.text}; font-size: 13px; }
        .ss-tracked-item span, .ss-pro-grid span { display: block; color: ${C.dim}; font-size: 11px; margin-top: 3px; }
        .ss-tracked-note { color: ${C.muted}; font-size: 13px; line-height: 1.5; margin-top: 16px; }
        .ss-pro-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
        .ss-pro-grid div { border-top: 1px solid rgba(126,179,244,0.14); padding-top: 10px; }
        .ss-map-shot { position: relative; }
        .ss-map-grid { position: relative; height: 118px; border: 1px solid rgba(126,179,244,0.2); border-radius: 7px; overflow: hidden; background: linear-gradient(135deg, rgba(78,192,109,0.22), transparent 38%), linear-gradient(45deg, transparent 0 44%, rgba(126,179,244,0.22) 45% 52%, transparent 53%), #0d233d; }
        .ss-map-grid::before, .ss-map-grid::after { content: ""; position: absolute; inset: 28% -10%; height: 2px; background: rgba(232,224,216,0.34); transform: rotate(-18deg); }
        .ss-map-grid::after { inset: 60% -10%; transform: rotate(14deg); }
        .ss-map-grid span { position: absolute; width: 13px; height: 13px; border-radius: 999px; background: ${C.amber}; box-shadow: 0 0 0 5px rgba(232,168,48,0.18); }
        .ss-audience-grid, .ss-price-grid, .ss-council-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
        .ss-audience, .ss-price, .ss-council-link { border: 1px solid ${C.border}; border-radius: 8px; background: ${C.bg2}; padding: 18px; text-decoration: none; color: ${C.text}; }
        .ss-audience strong, .ss-council-link strong { display: block; font-size: 16px; }
        .ss-audience span, .ss-council-link span, .ss-price p { display: block; color: ${C.muted}; font-size: 13px; line-height: 1.45; margin: 7px 0 0; }
        .ss-price span { color: ${C.dim}; text-transform: uppercase; letter-spacing: 1px; font-size: 11px; font-weight: 900; }
        .ss-price strong { display: block; color: ${C.amber2}; font-size: 28px; margin-top: 8px; }
        .ss-login-section { background: ${C.bg2}; border-top: 1px solid rgba(126,179,244,0.14); }
        .ss-login-copy { max-width: 760px; }
        .ss-footer { padding: 24px clamp(18px, 5vw, 72px) 38px; display: flex; flex-wrap: wrap; gap: 14px; justify-content: space-between; color: ${C.dim}; border-top: 1px solid rgba(126,179,244,0.14); }
        .ss-footer a { color: ${C.muted}; text-decoration: none; }
        @media (max-width: 960px) {
          .ss-header nav a:not(.ss-login-link) { display: none; }
          .ss-hero { min-height: auto; padding-top: 300px; }
          .ss-hero-scene { inset: 78px 14px auto; height: 270px; grid-template-columns: 1fr; overflow: hidden; opacity: 0.72; }
          .ss-hero-scene .ss-shot-stack { display: none; }
          .ss-shot-dashboard { min-width: 680px; transform: scale(0.76); transform-origin: top left; }
          .ss-grid, .ss-shots, .ss-login-section, .ss-stats, .ss-audience-grid, .ss-price-grid, .ss-council-grid { grid-template-columns: 1fr; }
          .ss-shot-row { grid-template-columns: 1fr 2fr 0.9fr; }
          .ss-shot-row span:nth-child(4), .ss-shot-row span:nth-child(5), .ss-shot-row strong:nth-child(6) { display: none; }
        }
        @media (max-width: 560px) {
          .ss-header { align-items: flex-start; }
          .ss-hero { padding-top: 250px; }
          .ss-shot-dashboard { transform: scale(0.58); }
          .ss-actions { display: grid; }
          .ss-button { width: 100%; }
          .ss-login-section { grid-template-columns: 1fr; }
        }
      `}</style>

      <header className="ss-header">
        <a href="/" aria-label="SiteScope home" style={{ textDecoration: 'none' }}><LogoMark /></a>
        <nav aria-label="SiteScope public navigation">
          <a href="#product">Product</a>
          <a href="/councils/">Council pages</a>
          <a href="#login" className="ss-login-link">Login</a>
        </nav>
      </header>

      <section className="ss-hero">
        <div className="ss-hero-scene" aria-hidden="true">
          <DashboardSnapshot />
          <div className="ss-shot-stack">
            <TrackedSnapshot />
            <ProSnapshot />
          </div>
        </div>
        <div className="ss-hero-copy">
          <p className="ss-eyebrow">Northern Rivers development application intelligence</p>
          <h1>SiteScope - See It First.</h1>
          <p className="ss-lead">Source-backed development application intelligence for the NSW Northern Rivers - Byron, Ballina, Lismore and Tweed.</p>
          <div className="ss-actions">
            <a className="ss-button" href="/app?auth=signup">Start free</a>
            <a className="ss-button secondary" href="/app">Log in</a>
            <a className="ss-button text" href="/councils/">Browse council pages</a>
          </div>
          <div className="ss-stats" aria-label="SiteScope coverage highlights">
            <Stat value="25K+" label="public DA records monitored across the SiteScope data layer" />
            <Stat value="4" label="Northern Rivers councils currently surfaced for public pages" />
            <Stat value="365d" label="recent DA window used for market and council visibility" />
            <Stat value="Pro" label="source-backed supply chain and document intelligence for paid users" />
          </div>
        </div>
      </section>

      <section className="ss-section" id="product">
        <h2>A development application desk for people who work from DAs all day.</h2>
        <p>SiteScope organises public planning data into a searchable workbench for finding, tracking and qualifying development activity. It is not a council or consent authority; it points users back to official source records for verification.</p>
        <div className="ss-grid">
          <Feature title="One searchable DA feed" body="Browse and filter lodged, assessing, exhibition and approved applications across supported councils with category, cost and dwelling signals." />
          <Feature title="Track the DAs that matter" body="Save applications, watch suburbs and pipeline stages, and keep private notes around the opportunities your team is following." />
          <Feature title="Verify from the source" body="Open official council records to check important claims. In-app PDF viewing is currently paused." />
        </div>
      </section>

      <section className="ss-section">
        <h2>Product snapshots</h2>
        <p>These illustrative previews use sample records to show the SiteScope experience: a dense dashboard, map-based discovery, tracked DA monitoring and document-backed Pro Intelligence without publishing paid contact fields.</p>
        <div className="ss-shots">
          <DashboardSnapshot />
          <div className="ss-shot-stack">
            <MapSnapshot />
            <TrackedSnapshot />
            <ProSnapshot />
          </div>
        </div>
      </section>

      <section className="ss-section">
        <h2>Who uses SiteScope?</h2>
        <p>SiteScope is for people who need to see development activity early, understand it quickly and follow the records that matter.</p>
        <div className="ss-audience-grid">
          <Audience title="Builders" body="Find new residential and commercial opportunities before they are old news." />
          <Audience title="Architects" body="Track local planning activity and understand what is being proposed nearby." />
          <Audience title="Town planners" body="Monitor council workloads, categories and source records across supported areas." />
          <Audience title="Developers" body="Scan the market for movement, constraints and comparable nearby applications." />
          <Audience title="Trades and suppliers" body="Spot early project signals and follow DAs as they move through assessment." />
          <Audience title="Owners" body="Keep an eye on local applications, status changes and official source links." />
        </div>
      </section>

      <section className="ss-section">
        <h2>Find a project. Check the detail. Decide your next step.</h2>
        <p>Start with a local application, understand what is proposed and keep a useful next step alongside it.</p>
        <div className="ss-grid">
          <Feature title="Start with your area" body="Choose your council or search a suburb, address or application reference. Public council pages are dated snapshots; sign in for the current feed." />
          <Feature title="Qualify the project" body="Review the scope, application type and known project team. Check source evidence and confirm whether work is available." />
          <Feature title="Choose a next step" body="Track relevant applications, save a private note and revisit changes before deciding who to approach." />
        </div>
      </section>

      <section className="ss-section">
        <h2>Northern Rivers council pages</h2>
        <p>Explore dated council snapshots, then open the app to search current records and track the projects that matter to you.</p>
        <div className="ss-council-grid">
          {councils.map(council => (
            <CouncilLink
              key={council.name}
              href={`/councils/${councilPageSlug(council)}/`}
              name={council.name}
              detail={COUNCIL_PAGE_DETAILS[council.shortName] || council.selectorLabel}
            />
          ))}
        </div>
      </section>

      <section className="ss-section" id="pricing">
        <h2>Start free. Explore Pro coverage.</h2>
        <p>Standard gives users a public DA discovery layer. Pro plans add broader workflow depth, tracked intelligence and document-backed project signals.</p>
        <p><strong>Online paid subscriptions are currently unavailable.</strong> Prices below describe planned coverage. <a href="mailto:support@sitescopeapp.com?subject=SiteScope%20demo">Ask about a Pro demo</a>.</p>
        <div className="ss-price-grid">
          <Price title="Standard" price="Free" body="Browse supported public DA records and council source links." />
          <Price title="Pro Starter" price="$99/mo" body="Pro intelligence for 1 selected council." />
          <Price title="Pro Growth" price="$149/mo" body="Pro intelligence for 2 selected councils." />
          <Price title="Pro Business" price="$199/mo" body="Pro intelligence for 3 selected councils." />
          <Price title="Pro Scale" price="$249/mo" body="Pro intelligence for all 4 supported councils." />
        </div>
        <p>All Pro prices include GST.</p>
      </section>

      <section className="ss-section ss-login-section" id="login">
        <div className="ss-login-copy">
          <p className="ss-eyebrow">Open the app</p>
          <h2>Log in or start a free account.</h2>
          <p>Use the app to search live DA records, track watch zones, manage your project pipeline and access Pro Intelligence where your account includes it.</p>
          <div className="ss-actions">
            <a className="ss-button" href="/app?auth=signup">Start free</a>
            <a className="ss-button secondary" href="/app">Log in</a>
          </div>
        </div>
      </section>

      <footer className="ss-footer">
        <span>See It First. SiteScope DA Tracking & Intelligence.</span>
        <span style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <a href="/councils/">Council pages</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/app">App login</a>
        </span>
      </footer>
    </main>
  );
}
